import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { OpportunityNote } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { Button } from '../../components/ui/Button.js';
import { formatDateTime } from '../../utils/date.js';
import {
  RichTextContent,
  RichTextEditor,
  collectNoteImageIds,
  hydrateNoteImages,
  serializeNoteImages,
} from '../../components/ui/RichText.js';
import {
  resolveOpportunityImages,
  uploadOpportunityImage,
  useCreateOpportunityNote,
  useDeleteOpportunityNote,
  useOpportunityNotes,
  useUpdateOpportunityNote,
} from './api.js';

interface OpportunityNotesSectionProps {
  opportunityId: string;
  canEdit: boolean;
}

const EMPTY_BODY = '<p></p>';

function isEmptyBody(html: string): boolean {
  // Treat as empty only when there is neither text nor an embedded image.
  return html.replace(/<[^>]*>/g, '').trim() === '' && !html.includes('psa-image:') && !html.includes('data-psa-id');
}

function NoteCard({
  note,
  canEdit,
  imageUrls,
  opportunityId,
  onSave,
  isSaving,
  onDelete,
  isDeleting,
}: {
  note: OpportunityNote;
  canEdit: boolean;
  imageUrls: Record<string, string>;
  opportunityId: string;
  onSave: (id: string, body: string, done: () => void) => void;
  isSaving: boolean;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}): JSX.Element {
  const [confirm, setConfirm] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const isEditing = draft !== null;
  const hydrated = useMemo(() => hydrateNoteImages(note.body, imageUrls), [note.body, imageUrls]);
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="text-xs text-slate-500">
          <span className="font-medium text-slate-700">{note.authorName}</span> ·{' '}
          {formatDateTime(note.createdAt)}
          {note.updatedAt ? (
            <span className="ml-1 italic">
              (edited{note.editedByName ? ` by ${note.editedByName}` : ''} ·{' '}
              {formatDateTime(note.updatedAt)})
            </span>
          ) : null}
        </div>
        {canEdit && !isEditing ? (
          confirm ? (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                className="px-2 py-0.5 text-xs text-rose-600 hover:bg-rose-50"
                onClick={() => onDelete(note.id)}
                isLoading={isDeleting}
              >
                Confirm
              </Button>
              <Button
                variant="ghost"
                className="px-2 py-0.5 text-xs"
                onClick={() => setConfirm(false)}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {/* Edit from the hydrated HTML so existing images render; the save path
                  serializes them back to their stable `psa-image:{id}` references. */}
              <Button
                variant="ghost"
                className="px-2 py-0.5 text-xs"
                onClick={() => setDraft(hydrated)}
              >
                Edit
              </Button>
              <Button
                variant="ghost"
                className="px-2 py-0.5 text-xs text-rose-600 hover:bg-rose-50"
                onClick={() => setConfirm(true)}
              >
                Delete
              </Button>
            </div>
          )
        ) : null}
      </div>
      {isEditing ? (
        <div className="flex flex-col gap-2">
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            placeholder="Edit note… (paste a screenshot to attach it)"
            onUploadImage={(file) => uploadOpportunityImage(opportunityId, file)}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              isLoading={isSaving}
              disabled={isEmptyBody(draft)}
              onClick={() => onSave(note.id, serializeNoteImages(draft), () => setDraft(null))}
            >
              Save note
            </Button>
          </div>
        </div>
      ) : (
        <RichTextContent html={hydrated} />
      )}
    </div>
  );
}

export function OpportunityNotesSection({
  opportunityId,
  canEdit,
}: OpportunityNotesSectionProps): JSX.Element {
  const [body, setBody] = useState(EMPTY_BODY);

  const notesQuery = useOpportunityNotes(opportunityId);
  const createMutation = useCreateOpportunityNote();
  const updateMutation = useUpdateOpportunityNote();
  const deleteMutation = useDeleteOpportunityNote();

  const notes = notesQuery.data ?? [];

  // Resolve every inline-image reference across all notes to a fresh display URL.
  const imageIds = useMemo(() => {
    const set = new Set<string>();
    for (const note of notes) {
      for (const id of collectNoteImageIds(note.body)) set.add(id);
    }
    return [...set].sort();
  }, [notes]);

  const imagesQuery = useQuery({
    queryKey: ['opportunities', opportunityId, 'note-images', imageIds],
    queryFn: () => resolveOpportunityImages(opportunityId, imageIds),
    enabled: imageIds.length > 0,
    staleTime: 40 * 60 * 1000,
  });
  const imageUrls = imagesQuery.data ?? {};

  const generalError = createMutation.isError
    ? createMutation.error instanceof ApiError
      ? createMutation.error.message
      : 'Failed to add note.'
    : updateMutation.isError
      ? updateMutation.error instanceof ApiError
        ? updateMutation.error.message
        : 'Failed to save note.'
      : null;

  const handleAdd = (): void => {
    if (isEmptyBody(body)) {
      return;
    }
    createMutation.mutate(
      { opportunityId, input: { body: serializeNoteImages(body) } },
      { onSuccess: () => setBody(EMPTY_BODY) },
    );
  };

  const handleSave = (noteId: string, nextBody: string, done: () => void): void => {
    if (isEmptyBody(nextBody)) return;
    updateMutation.mutate({ opportunityId, noteId, input: { body: nextBody } }, { onSuccess: done });
  };

  const handleDelete = (noteId: string): void => {
    deleteMutation.mutate({ opportunityId, noteId });
  };

  return (
    <div className="flex flex-col gap-4">
      {canEdit ? (
        <div className="flex flex-col gap-2">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}
          <RichTextEditor
            value={body}
            onChange={setBody}
            placeholder="Add a note… (paste a screenshot to attach it)"
            onUploadImage={(file) => uploadOpportunityImage(opportunityId, file)}
          />
          <div className="flex justify-end">
            <Button
              onClick={handleAdd}
              isLoading={createMutation.isPending}
              disabled={isEmptyBody(body)}
            >
              Add note
            </Button>
          </div>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        {notesQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading notes…</p>
        ) : notes.length === 0 ? (
          <p className="text-sm text-slate-500">No notes yet.</p>
        ) : (
          notes.map((note) => (
            <NoteCard
              key={note.id}
              note={note}
              canEdit={canEdit}
              imageUrls={imageUrls}
              opportunityId={opportunityId}
              onSave={handleSave}
              isSaving={updateMutation.isPending}
              onDelete={handleDelete}
              isDeleting={deleteMutation.isPending}
            />
          ))
        )}
      </div>
    </div>
  );
}
